import { useMemo } from 'react';
import { Download, FileCheck2, ScrollText } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ConfidenceBar,
  KeyValueGrid,
  MetricCard,
  Tabs,
  type TabItem,
} from '@/components';
import { frameworkInFocus, useActiveFocus, useData, useUI } from '@/data/stores';
import { demoTimestamp, downloadJson } from '@/lib/export';
import './compliance.css';
import type { ComplianceFramework } from '@/types';

export function ComplianceView() {
  const allCompliance = useData((s) => s.compliance);
  const alerts = useData((s) => s.alerts);
  const notify = useUI((s) => s.notifyAction);
  const focus = useActiveFocus();
  // Show only frameworks applicable in the active focus mode. The "applicable
  // frameworks" list is curated per focus, so a user in Ukraine focus sees
  // UA-CIP + NIS2, while EU-CNI sees the broader Western European stack.
  const compliance = useMemo(
    () =>
      focus.applicable_frameworks.length === 0
        ? allCompliance
        : allCompliance.filter((f) => frameworkInFocus(focus, f)),
    [allCompliance, focus],
  );

  const overall = useMemo(() => {
    if (compliance.length === 0) return 0;
    return Math.round(
      compliance.reduce((s, f) => s + f.overall_score, 0) / compliance.length,
    );
  }, [compliance]);

  const total = useMemo(
    () => compliance.reduce((s, f) => s + f.controls.length, 0),
    [compliance],
  );

  const compliant = useMemo(
    () =>
      compliance.reduce(
        (s, f) => s + f.controls.filter((c) => c.status === 'compliant').length,
        0,
      ),
    [compliance],
  );
  const partial = useMemo(
    () =>
      compliance.reduce(
        (s, f) => s + f.controls.filter((c) => c.status === 'partial').length,
        0,
      ),
    [compliance],
  );

  const incidents24h = useMemo(
    () =>
      alerts.filter(
        (a) =>
          new Date(a.detected_at).getTime() >
          Date.now() - 24 * 3600 * 1000 - 6 * 24 * 3600 * 1000, // last 7d for demo
      ).length,
    [alerts],
  );

  const tabs: TabItem[] = compliance.map((f) => ({
    key: f.framework_id,
    label: f.framework_id.replace(/_/g, ' '),
    badge: <span>{f.overall_score}%</span>,
    content: <FrameworkPanel framework={f} />,
  }));

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Compliance & Reporting</h1>
          <p className="caption">
            Continuous evidence generation across NIS2, NERC CIP, IEC 62443, NIST CSF 2.0,
            ISO 27001, COMAH, and more - automated 24h/72h NIS2 incident notifications.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<ScrollText size={14} strokeWidth={1.5} />}
            onClick={() => {
              const critical = alerts.filter((a) => a.severity >= 4 && a.status !== 'resolved');
              downloadJson(`iris-nis2-24h-${demoTimestamp()}.json`, {
                type: 'NIS2_24H_EARLY_WARNING',
                generated_at: new Date().toISOString(),
                focus_mode: focus.id,
                incident_count: critical.length,
                incidents: critical.slice(0, 20),
              });
              notify({
                kind: 'success',
                title: 'NIS2 24h notification generated',
                body: `${critical.length} high-severity incident candidates included in the draft.`,
              });
            }}
          >
            NIS2 24h notification
          </Button>
          <Button
            icon={<FileCheck2 size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-incident-72h-${demoTimestamp()}.json`, {
                type: 'NIS2_72H_INCIDENT_REPORT',
                generated_at: new Date().toISOString(),
                focus_mode: focus.id,
                executive_summary: 'Demo-generated incident report package from Iris seed data.',
                incidents: alerts.filter((a) => a.severity >= 3).slice(0, 30),
                compliance_scope: compliance.map((f) => f.framework_id),
              });
              notify({
                kind: 'success',
                title: '72h incident report generated',
                body: 'Draft report package downloaded for review.',
              });
            }}
          >
            72h incident report
          </Button>
          <Button
            variant="primary"
            icon={<Download size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-evidence-pack-${focus.id}-${demoTimestamp()}.json`, {
                generated_at: new Date().toISOString(),
                focus_mode: focus.id,
                overall_score: overall,
                frameworks: compliance,
              });
              notify({
                kind: 'success',
                title: 'Evidence pack exported',
                body: `${compliance.length} framework pack downloaded for ${focus.label}.`,
              });
            }}
          >
            Export evidence pack
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard
          label="Overall score"
          value={`${overall}%`}
          status={
            overall >= 90
              ? 'var(--status-operational)'
              : overall >= 75
                ? 'var(--status-degraded)'
                : 'var(--status-critical)'
          }
        />
        <MetricCard label="Frameworks" value={compliance.length} />
        <MetricCard label="Total controls" value={total} />
        <MetricCard label="Compliant" value={compliant} status="var(--status-operational)" />
        <MetricCard label="Partial" value={partial} status="var(--status-degraded)" />
        <MetricCard label="Incidents 7d" value={incidents24h} caption="auto-notifiable" />
      </section>

      <Tabs items={tabs} />
    </div>
  );
}

function FrameworkPanel({ framework }: { framework: ComplianceFramework }) {
  return (
    <div className="iris-compliance-grid">
      <Card
        title={framework.name}
        subtitle={`${framework.jurisdiction} · ${framework.sector}`}
        actions={<Badge color="var(--status-info)">{framework.framework_id}</Badge>}
      >
        <KeyValueGrid
          cols={2}
          items={[
            { label: 'Overall', value: <ConfidenceBar value={framework.overall_score} /> },
            { label: 'Next audit', value: framework.next_audit ?? '-', mono: true },
            { label: 'Applicable assets', value: framework.applicable_assets.length, mono: true },
            { label: 'Controls', value: framework.controls.length, mono: true },
          ]}
        />
      </Card>
      {framework.controls.map((c) => (
        <Card
          key={c.control_id}
          title={c.title}
          subtitle={c.control_id}
          accent={
            c.status === 'compliant'
              ? 'var(--status-operational)'
              : c.status === 'partial'
                ? 'var(--status-degraded)'
                : c.status === 'non_compliant'
                  ? 'var(--status-critical)'
                  : 'var(--text-secondary)'
          }
        >
          <p>{c.description}</p>
          <KeyValueGrid
            cols={2}
            items={[
              {
                label: 'Status',
                value: (
                  <Badge
                    color={
                      c.status === 'compliant'
                        ? 'var(--status-operational)'
                        : c.status === 'partial'
                          ? 'var(--status-degraded)'
                          : c.status === 'non_compliant'
                            ? 'var(--status-critical)'
                            : 'var(--text-secondary)'
                    }
                  >
                    {c.status.replace(/_/g, ' ')}
                  </Badge>
                ),
              },
              { label: 'Evidence', value: c.evidence_count, mono: true },
              { label: 'Owner', value: c.owner ?? '-' },
              {
                label: 'Last audited',
                value: c.last_audited ?? '-',
                mono: true,
              },
            ]}
          />
          {c.remediation && (
            <div className="iris-compliance-remediation">
              <span className="caption">Remediation</span>
              <p>{c.remediation}</p>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
